import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { TruckIcon, Eye, EyeOff, MailCheck, Loader2 } from "lucide-react";

// reCAPTCHA v3 site key is build-time injected via CRA. When unset (dev/local),
// the page skips loading Google's script entirely and posts captcha_token: null —
// the backend dev-bypasses verification when no secret is configured.
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_SRC = RECAPTCHA_SITE_KEY
  ? `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`
  : null;

const VOLUME_OPTIONS = [
  { value: "<1000", label: "Less than 1,000" },
  { value: "1000-10000", label: "1,000 – 10,000" },
  { value: "10000-50000", label: "10,000 – 50,000" },
  { value: "50000+", label: "50,000+" },
];

const signupSchema = z.object({
  name: z.string().trim().min(2, "Please enter your full name"),
  email: z.string().trim().email("Enter a valid email address"),
  phone: z
    .string()
    .trim()
    .regex(/^[0-9]{10}$/, "Enter a 10-digit mobile number"),
  password: z
    .string()
    .min(8, "At least 8 characters")
    .regex(/[A-Za-z]/, "Include at least one letter")
    .regex(/[0-9]/, "Include at least one number"),
  company_name: z.string().trim().min(2, "Enter your company name"),
  brand_name: z.string().trim().optional(),
  monthly_volume: z.string().min(1, "Select an expected monthly volume"),
  consent: z.literal(true, {
    errorMap: () => ({ message: "You must accept the terms to continue" }),
  }),
});

// Pull a human-readable detail out of an axios error (FastAPI sends string or
// validation-array `detail`), matching the B2COrders convention.
const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const SignupPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      company_name: "",
      brand_name: "",
      monthly_volume: "",
      consent: false,
    },
  });

  const volumeValue = watch("monthly_volume");
  const consentValue = watch("consent");

  // Load the reCAPTCHA v3 script only when a site key is configured.
  useEffect(() => {
    if (!RECAPTCHA_SRC) return;
    if (document.querySelector(`script[src="${RECAPTCHA_SRC}"]`)) return;
    const script = document.createElement("script");
    script.src = RECAPTCHA_SRC;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  // Resolve a captcha token for this submit. Returns null when no key is set
  // (dev) or when the grecaptcha runtime is unavailable, letting the backend
  // dev-bypass decide.
  const getCaptchaToken = useCallback(async () => {
    if (!RECAPTCHA_SITE_KEY) return null;
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha || !grecaptcha.execute) return null;
    try {
      return await new Promise((resolve) => {
        grecaptcha.ready(() => {
          grecaptcha
            .execute(RECAPTCHA_SITE_KEY, { action: "signup" })
            .then((token) => resolve(token || null))
            .catch(() => resolve(null));
        });
      });
    } catch {
      return null;
    }
  }, []);

  const onSubmit = async (values) => {
    const captchaToken = await getCaptchaToken();
    try {
      const payload = {
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        password: values.password,
        company_name: values.company_name.trim(),
        brand_name: values.brand_name?.trim() || null,
        monthly_volume: values.monthly_volume || null,
        captcha_token: captchaToken,
        consent: values.consent === true,
      };
      const { data } = await axios.post(`${API}/auth/signup`, payload);
      setSuccessMessage(
        data?.message || "Account created — you can log in now."
      );
      setSubmitted(true);
    } catch (error) {
      toast.error(errDetail(error, "Could not create your account. Please try again."));
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col bg-gradient-to-br from-slate-50 via-teal-50 to-emerald-50">
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          {/* Compact brand header */}
          <div className="mb-6 text-center space-y-3">
            <div className="flex items-center justify-center gap-3">
              <div className="p-2.5 rounded-xl bg-brand">
                <TruckIcon className="w-7 h-7 text-white" />
              </div>
              <div className="text-left">
                <h1 className="text-2xl font-bold text-slate-800 leading-none">EdgeLMS</h1>
                <p className="text-xs font-medium text-slate-500 tracking-wide">by Edgistify</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 px-4">
              Start shipping your D2C orders across India in minutes
            </p>
          </div>

          <Card className="shadow-2xl border-0">
            {submitted ? (
              <CardContent className="py-10 text-center space-y-4">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-bg">
                  <MailCheck className="h-7 w-7 text-brand" />
                </div>
                <CardTitle className="text-xl font-semibold">You’re all set!</CardTitle>
                <p className="text-sm text-slate-600 px-2 leading-relaxed">{successMessage}</p>
                <p className="text-xs text-slate-500 px-2">
                  We’ve emailed you a welcome note with next steps.
                </p>
                <Link
                  to="/login"
                  data-testid="signup-back-to-login"
                  className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-base font-medium text-white transition-colors hover:bg-brand-dark"
                >
                  Log in now
                </Link>
              </CardContent>
            ) : (
              <>
                <CardHeader className="space-y-1 pb-6">
                  <CardTitle className="text-xl font-semibold">Create your account</CardTitle>
                  <CardDescription>
                    Self-serve seller signup — create your account and you’re ready to ship
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Full name</Label>
                      <Input
                        id="signup-name"
                        data-testid="signup-name-input"
                        type="text"
                        placeholder="Your name"
                        autoComplete="name"
                        className="text-base h-11"
                        aria-invalid={!!errors.name}
                        {...register("name")}
                      />
                      {errors.name && (
                        <p className="text-xs text-destructive">{errors.name.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        data-testid="signup-email-input"
                        type="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        className="text-base h-11"
                        aria-invalid={!!errors.email}
                        {...register("email")}
                      />
                      {errors.email && (
                        <p className="text-xs text-destructive">{errors.email.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-phone">Mobile number</Label>
                      <Input
                        id="signup-phone"
                        data-testid="signup-phone-input"
                        type="tel"
                        inputMode="numeric"
                        placeholder="10-digit mobile number"
                        autoComplete="tel"
                        className="text-base h-11"
                        aria-invalid={!!errors.phone}
                        {...register("phone")}
                      />
                      {errors.phone && (
                        <p className="text-xs text-destructive">{errors.phone.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="signup-password"
                          data-testid="signup-password-input"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          autoComplete="new-password"
                          className="pr-10 text-base h-11"
                          aria-invalid={!!errors.password}
                          {...register("password")}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          data-testid="signup-toggle-password"
                        >
                          {/* Icon reflects current visibility: open eye = visible, slashed = hidden. */}
                          {showPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                      </div>
                      {errors.password ? (
                        <p className="text-xs text-destructive">{errors.password.message}</p>
                      ) : (
                        <p className="text-xs text-slate-500">8+ chars, a letter and a number</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-company">Company name</Label>
                      <Input
                        id="signup-company"
                        data-testid="signup-company-input"
                        type="text"
                        placeholder="Registered business name"
                        autoComplete="organization"
                        className="text-base h-11"
                        aria-invalid={!!errors.company_name}
                        {...register("company_name")}
                      />
                      {errors.company_name && (
                        <p className="text-xs text-destructive">{errors.company_name.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-brand">
                        Brand name <span className="text-slate-400">(optional)</span>
                      </Label>
                      <Input
                        id="signup-brand"
                        data-testid="signup-brand-input"
                        type="text"
                        placeholder="Defaults to your company name"
                        className="text-base h-11"
                        {...register("brand_name")}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-volume">Expected monthly shipments</Label>
                      <Select
                        value={volumeValue}
                        onValueChange={(v) =>
                          setValue("monthly_volume", v, { shouldValidate: true })
                        }
                      >
                        <SelectTrigger
                          id="signup-volume"
                          data-testid="signup-volume-select"
                          className="text-base h-11"
                          aria-invalid={!!errors.monthly_volume}
                        >
                          <SelectValue placeholder="Select a range" />
                        </SelectTrigger>
                        <SelectContent>
                          {VOLUME_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.monthly_volume && (
                        <p className="text-xs text-destructive">{errors.monthly_volume.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-start gap-2.5">
                        <Checkbox
                          id="signup-consent"
                          data-testid="signup-consent-checkbox"
                          className="mt-0.5"
                          checked={consentValue === true}
                          onCheckedChange={(checked) =>
                            setValue("consent", checked === true, { shouldValidate: true })
                          }
                        />
                        <Label
                          htmlFor="signup-consent"
                          className="text-sm font-normal leading-snug text-slate-600"
                        >
                          I agree to the EdgeLMS Terms of Service and Privacy Policy.
                        </Label>
                      </div>
                      {errors.consent && (
                        <p className="text-xs text-destructive">{errors.consent.message}</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      data-testid="signup-submit-button"
                      className="w-full h-11"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating account...
                        </>
                      ) : (
                        "Create account"
                      )}
                    </Button>
                  </form>

                  {RECAPTCHA_SITE_KEY && (
                    <p className="mt-4 text-center text-[0.7rem] leading-snug text-slate-400">
                      This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of
                      Service apply.
                    </p>
                  )}

                  <p className="mt-6 text-center text-sm text-slate-600">
                    Already have an account?{" "}
                    <Link
                      to="/login"
                      data-testid="signup-login-link"
                      className="font-medium text-brand hover:underline"
                    >
                      Log in
                    </Link>
                  </p>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
      <footer className="py-4 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} OptiSupply Chain Solution Pvt. Ltd. · EdgeLMS
      </footer>
    </div>
  );
};

export default SignupPage;
